import { createSelector } from 'reselect';

import { getProp } from '@mews/fp';
import {
    DateService,
    DateTimeService,
    DateTimeUtc,
    DateTimeUtcService,
    WallClockDateService,
} from '@mews/framework/datetime';

import { getAvailabilityBlockIdFromQuery } from '../../modules/app/selectors';
import {
    getAvailabilityBlockEndUtc,
    getAvailabilityBlockName,
    getAvailabilityBlockServiceId,
    getAvailabilityBlockStartUtc,
} from '../../modules/entities/availabilityBlocks/selectors';
import { ApplicationState } from '../../types';

import { getSelectedCityEnterpriseTimezone } from './selectedCityEnterprises';
import { getSelectedEnterpriseConfigurationServiceId } from './selectedEnterpriseConfiguration';

export enum InvalidAvailabilityBlockReason {
    Expired = 'Expired',
    Misconfigured = 'Misconfigured',
    MissingData = 'MissingData',
}

type IsValidAvailabilityBlockWithReasonReturnType =
    | { isValid: true }
    | { isValid: false; reason: InvalidAvailabilityBlockReason };

export const isValidAvailabilityBlockWithReason = createSelector(
    getAvailabilityBlockServiceId,
    getAvailabilityBlockStartUtc,
    getAvailabilityBlockEndUtc,
    getProp<DateTimeUtc>()('nowUtc'),
    getSelectedEnterpriseConfigurationServiceId,
    getSelectedCityEnterpriseTimezone,
    (
        serviceId,
        startUtc,
        endUtc,
        nowUtc,
        configurationServiceId,
        timezone: string
    ): IsValidAvailabilityBlockWithReasonReturnType => {
        if (serviceId == null || endUtc == null || startUtc == null) {
            return { isValid: false, reason: InvalidAvailabilityBlockReason.MissingData };
        }

        if (configurationServiceId !== serviceId) {
            return { isValid: false, reason: InvalidAvailabilityBlockReason.Misconfigured };
        }

        const endDateUtc = DateTimeUtcService.parse(endUtc);

        const endDate = DateTimeService.toDate(DateTimeService.fromUtc(endDateUtc, timezone));
        const nowDate = DateTimeService.toDate(DateTimeService.fromUtc(nowUtc, timezone));

        const isCurrent = DateService.isGreaterOrEqual(endDate, nowDate);

        return isCurrent ? { isValid: true } : { isValid: false, reason: InvalidAvailabilityBlockReason.Expired };
    }
);

export const isValidAvailabilityBlock = createSelector(
    isValidAvailabilityBlockWithReason,
    (isValid) => isValid.isValid
);

export const getValidQueryParamAvailabilityBlockId = (state: ApplicationState, { nowUtc }: { nowUtc: DateTimeUtc }) => {
    const availabilityBlockId = getAvailabilityBlockIdFromQuery(state);
    return availabilityBlockId != null && isValidAvailabilityBlock(state, { availabilityBlockId, nowUtc })
        ? availabilityBlockId
        : undefined;
};

export const getValidAvailabilityBlockInterval = createSelector(
    isValidAvailabilityBlock,
    getAvailabilityBlockStartUtc,
    getAvailabilityBlockEndUtc,
    getSelectedCityEnterpriseTimezone,
    (isValid, startUtc, endUtc, timezone) => {
        if (!isValid || startUtc == null || endUtc == null) {
            return {};
        }

        const startDateUtc = DateTimeUtcService.parse(startUtc);
        const endDateUtc = DateTimeUtcService.parse(endUtc);
        const startDate = DateTimeService.toDate(DateTimeService.fromUtc(startDateUtc, timezone));
        const endDate = DateTimeService.toDate(DateTimeService.fromUtc(endDateUtc, timezone));

        return {
            availabilityBlockStartDate: WallClockDateService.parse(startDate.value),
            availabilityBlockEndDate: WallClockDateService.parse(endDate.value),
        };
    }
);

export const getValidAvailabilityBlockName = createSelector(
    isValidAvailabilityBlock,
    getAvailabilityBlockName,
    (isValid, availabilityBlockName) => (isValid && availabilityBlockName != null ? availabilityBlockName : null)
);

export interface WithNowUtcProps {
    nowUtc: DateTimeUtc;
}

export const getCurrentValidAvailabilityBlockName = (state: ApplicationState, { nowUtc }: WithNowUtcProps) => {
    const availabilityBlockId = getAvailabilityBlockIdFromQuery(state);

    return availabilityBlockId != null ? getValidAvailabilityBlockName(state, { availabilityBlockId, nowUtc }) : null;
};
